/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import isExpiredDeliveryInEventTriggeredCampaign from 'cio/utils/campaigns/is-expired-delivery-in-event-triggered-campaign';
import { task } from 'ember-concurrency';
import DeliveryModel from 'cio/models/delivery';
import AnalyticsService from 'cio/services/analytics';
import AlertsService from 'cio/services/alerts';
import AuthService from 'cio/services/auth';
import MessageSettingsService from 'cio/services/message-settings';
import DeliveryService from 'cio/services/delivery';
import DeliveryDetailModel from 'cio/models/delivery-detail';
import CampaignModel from 'cio/models/campaign';
import CustomerModel from 'cio/models/customer';
import { IdentifierType } from 'cio/utils/identifiers';
import { isEmpty } from '@ember/utils';

export interface PreviewSignature {
  Args: {
    delivery: DeliveryModel;
    detail: DeliveryDetailModel | null;
    campaign: CampaignModel;
    customerId: string;
    customr: CustomerModel;
    customerDeleted: boolean;
    identifierType: IdentifierType;
    hasReadOnlyPermission: boolean;
  };
}
export default class Preview extends Component<PreviewSignature> {
  @service analytics!: AnalyticsService;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service messageSettings!: MessageSettingsService;
  @service('delivery') deliveryService!: DeliveryService;

  @tracked redeliveryDisabled = false;
  @tracked showRetryModal = false;

  get showWhitespace() {
    let unformattedActionTypes = [
      'push_action',
      'slack_action',
      'twilio_action',
      'urban_airship_action',
      'in_app_action',
      'webhook_action',
    ];

    return unformattedActionTypes.includes(this.args.delivery.actionType);
  }

  get redeliverButtonDisabled() {
    return (
      (this.isHideBody ?? false) ||
      this.redeliveryDisabled ||
      isExpiredDeliveryInEventTriggeredCampaign(this.args.delivery, this.args.campaign)
    );
  }

  get isDefaultPushRecipient() {
    if (this.args.delivery.actionType !== 'push_action') {
      return false;
    }

    let recipient = this.args.detail?.recipient ?? '';
    return recipient === 'last_used' || recipient === 'all';
  }

  get isInAppAction() {
    return this.args.delivery.actionType === 'in_app_action';
  }

  get hasHeaders() {
    let { replyTo, bcc } = this.args.delivery;
    return replyTo || bcc || (this.args.detail?.headers ?? []).length > 0;
  }

  get hasSubject() {
    return this.args.detail?.subject != null;
  }

  get hasPreheaderText() {
    return !isEmpty(this.args.detail?.preheaderText);
  }

  get isHideBody() {
    return this.args.detail?.hideBody;
  }
  get canRetryDelivery() {
    let { delivery } = this.args;
    return (
      delivery.isFailed || delivery.isBounced || delivery.isSpammed || delivery.isSuppressed || delivery.isUndeliverable
    );
  }

  get deliveryIsDraftedOrFailed() {
    return this.args.delivery.isDrafted || this.args.delivery.isFailed;
  }

  get deliveryIsFailedOrUndeliverable() {
    return this.args.delivery.isFailed || this.args.delivery.isUndeliverable;
  }

  get isEmail() {
    return this.args.delivery.actionType === 'email_action';
  }

  get isCampaign() {
    return this.args.campaign != null;
  }

  get canEditDelivery() {
    return this.isEmail && this.isCampaign && this.args.delivery.isDrafted;
  }

  get canResendDelivery() {
    return this.args.delivery.isSent || this.args.delivery.isDelivered;
  }

  get canDeleteDelivery() {
    return this.isCampaign && this.args.delivery.isDrafted;
  }

  get showSendButton() {
    return this.args.delivery.isAttempted || this.args.delivery.isDrafted;
  }

  @action
  async destroyAction() {
    let { alerts } = this;

    alerts.sticky('delivery.delete', 'Deleting your draft…');

    try {
      await this.deliveryService.delete(this.args.delivery.id);
      alerts.success('delivery.delete', 'Your draft will be deleted soon');
    } catch {
      alerts.error('delivery.delete', 'Failed to delete draft');
    }
  }

  private trackRetryEvent(eventKey: any, additionalParams = {}) {
    let params = {
      count: 1,
      statuses: [this.args.delivery.displayState],
      ...additionalParams,
    };

    this.analytics.track('message', eventKey, params);
  }

  @action
  cancelRetry() {
    this.showRetryModal = false;
    this.trackRetryEvent('retry_send_canceled');
  }

  @action
  retry() {
    if (this.args.delivery.isUndeliverable && this.messageSettings.isMessagingLimitEnabled) {
      this.showRetryModal = true;
      this.trackRetryEvent('retry_send_started');
    } else {
      this.confirmRetry();
      this.trackRetryEvent('retry_send_confirmed', { overrideLimit: false });
    }
  }

  @action
  async confirmRetry(overrideLimit = false) {
    let { alerts } = this;

    alerts.sticky('delivery.retry', 'Retrying your message…');

    try {
      await this.deliveryService.retry(this.args.delivery.id, { overrideLimit });
      alerts.success('delivery.retry', 'Your message will be retried soon');
    } catch {
      alerts.error('delivery.retry', 'Failed to retry message');
    }

    this.showRetryModal = false;
  }

  @action
  async redeliver({ tracked }: any) {
    let { label, id } = this.args.delivery;
    let { alerts } = this;

    alerts.sticky('delivery.redeliver', `Sending this ${label} again…`);

    try {
      await this.deliveryService.redeliver(id, { tracked });
      alerts.success('delivery.redeliver', `Your ${label} will be sent again soon`);
      this.redeliveryDisabled = true;
    } catch {
      alerts.error('delivery.redeliver', `Failed to resend this ${label}`);
    }
  }

  send = task({ drop: true }, async () => {
    this.alerts.sticky('delivery.deliver', 'Sending your message…');

    try {
      await this.deliveryService.deliver(this.args.delivery.id);
      this.alerts.success('delivery.deliver', 'Your message will be sent soon');
    } catch {
      this.alerts.error('delivery.deliver', 'Failed to send your message');
    }
  });
}
